import { FC } from 'react';
import {
  DollarSign,
  TrendingUp,
  Clock,
  CreditCard,
  AlertCircle,
  Calendar,
  BarChart3,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import { useTherapistEarnings } from '../../../hooks/useTherapistDashboard';
import { EarningsChart } from './EarningsChart';

/**
 * Format date string to readable format
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface EarningsCardProps {
  title: string;
  amount: number;
  icon: React.ReactNode;
  accentColor: 'green' | 'blue' | 'purple' | 'amber';
}

function EarningsCard({ title, amount, icon, accentColor }: EarningsCardProps) {
  const colorClasses = {
    green: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      icon: 'text-green-600 dark:text-green-400',
      border: 'border-green-200 dark:border-green-800',
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      icon: 'text-blue-600 dark:text-blue-400',
      border: 'border-blue-200 dark:border-blue-800',
    },
    purple: {
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      icon: 'text-purple-600 dark:text-purple-400',
      border: 'border-purple-200 dark:border-purple-800',
    },
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      icon: 'text-amber-600 dark:text-amber-400',
      border: 'border-amber-200 dark:border-amber-800',
    },
  };

  const colors = colorClasses[accentColor];

  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800 rounded-lg border p-4 sm:p-6',
        'border-gray-200 dark:border-slate-700',
        'transition-shadow hover:shadow-md'
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-500 dark:text-slate-400">
          {title}
        </span>
        <div className={cn('p-2 rounded-lg', colors.bg, colors.border, 'border')}>
          <div className={cn('w-5 h-5', colors.icon)}>{icon}</div>
        </div>
      </div>
      <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-100">
        {formatCurrency(amount)}
      </p>
    </div>
  );
}

export const EarningsSection: FC = () => {
  const { data: earnings, isLoading, error, refetch } = useTherapistEarnings();

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Earnings</h2>
          <p className="text-gray-600 dark:text-slate-400 mt-1">
            Track your income and payouts
          </p>
        </div>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Earnings</h2>
          <p className="text-gray-600 dark:text-slate-400 mt-1">
            Track your income and payouts
          </p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-8 text-center border border-red-200 dark:border-red-800">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            Failed to Load Earnings
          </h3>
          <p className="text-red-600 dark:text-red-300 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state (no earnings data)
  if (!earnings) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Earnings</h2>
          <p className="text-gray-600 dark:text-slate-400 mt-1">
            Track your income and payouts
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-8 text-center border border-gray-200 dark:border-slate-700">
          <DollarSign className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-slate-300 mb-2">
            No Earnings Yet
          </h3>
          <p className="text-gray-500 dark:text-slate-400">
            Complete appointments to start tracking your earnings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Earnings</h2>
        <p className="text-gray-600 dark:text-slate-400 mt-1">
          Track your income and payouts
        </p>
      </div>

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <EarningsCard
          title="Today"
          amount={earnings.today}
          icon={<DollarSign className="w-5 h-5" />}
          accentColor="green"
        />
        <EarningsCard
          title="This Week"
          amount={earnings.week}
          icon={<TrendingUp className="w-5 h-5" />}
          accentColor="blue"
        />
        <EarningsCard
          title="This Month"
          amount={earnings.month}
          icon={<Calendar className="w-5 h-5" />}
          accentColor="purple"
        />
        <EarningsCard
          title="This Year"
          amount={earnings.year}
          icon={<TrendingUp className="w-5 h-5" />}
          accentColor="amber"
        />
      </div>

      {/* Pending Payout & Last Payout Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pending Payout */}
        <div
          className={cn(
            'bg-white dark:bg-slate-800 rounded-lg border p-6',
            'border-gray-200 dark:border-slate-700'
          )}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              Pending Payout
            </h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            {formatCurrency(earnings.pendingPayout)}
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Will be deposited on your next payout date
          </p>
        </div>

        {/* Last Payout */}
        <div
          className={cn(
            'bg-white dark:bg-slate-800 rounded-lg border p-6',
            'border-gray-200 dark:border-slate-700'
          )}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <CreditCard className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              Last Payout
            </h3>
          </div>
          {earnings.lastPayout ? (
            <>
              <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">
                {formatCurrency(earnings.lastPayout.amount)}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-slate-400">
                <span>{formatDate(earnings.lastPayout.date)}</span>
                <span className="capitalize">via {earnings.lastPayout.method}</span>
              </div>
            </>
          ) : (
            <p className="text-gray-500 dark:text-slate-400">No payouts yet</p>
          )}
        </div>
      </div>

      {/* Earnings Chart */}
      {earnings.earningsHistory.length > 0 && (
        <div
          className={cn(
            'bg-white dark:bg-slate-800 rounded-lg border p-4 sm:p-6',
            'border-gray-200 dark:border-slate-700'
          )}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Earnings Trend
              </h3>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Daily earnings over the past period
              </p>
            </div>
          </div>
          <EarningsChart earningsHistory={earnings.earningsHistory} />
        </div>
      )}

      {/* Earnings History */}
      <div
        className={cn(
          'bg-white dark:bg-slate-800 rounded-lg border',
          'border-gray-200 dark:border-slate-700'
        )}
      >
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
            Earnings History
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Your recent daily earnings
          </p>
        </div>

        {earnings.earningsHistory.length > 0 ? (
          <div className="overflow-x-auto">
            {/* Desktop Table View */}
            <table className="w-full hidden sm:table">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Appointments
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Earnings
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {earnings.earningsHistory.map((entry, index) => (
                  <tr
                    key={entry.date}
                    className={cn(
                      'hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors',
                      index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50/50 dark:bg-slate-800/50'
                    )}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-slate-100">
                      {formatDate(entry.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-slate-300">
                      {entry.appointmentsCount} {entry.appointmentsCount === 1 ? 'appointment' : 'appointments'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-slate-100">
                      {formatCurrency(entry.earnings)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile List View */}
            <div className="sm:hidden divide-y divide-gray-200 dark:divide-slate-700">
              {earnings.earningsHistory.map((entry) => (
                <div key={entry.date} className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                      {formatDate(entry.date)}
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-slate-100">
                      {formatCurrency(entry.earnings)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {entry.appointmentsCount} {entry.appointmentsCount === 1 ? 'appointment' : 'appointments'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            <Calendar className="w-10 h-10 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-slate-400">No earnings history yet</p>
          </div>
        )}
      </div>
    </div>
  );
};
